import { AppApi, ScreenView, el, panel, button } from '../ui/app-api'
import { getSettings, updateSettings } from '../../services/settings'
import { buildResolutionModes, nativePanelSize } from '../../engine/resolution'
import { Quality } from '../../types'

export function createSettingsScreen(api: AppApi): ScreenView {
  const root = el('div', { class: 'screen' })
  const p = panel('Settings')

  const slider = (label: string, key: 'master' | 'music' | 'sfx') => {
    const input = el('input', { attrs: { type: 'range', min: '0', max: '1', step: '0.05' } }) as HTMLInputElement
    input.addEventListener('input', () => {
      updateSettings({ [key]: Number(input.value) })
      if (key !== 'music') api.audio.play('click')
    })
    const field = el('div', { class: 'field' }, el('label', { text: label }), input)
    return { field, input }
  }

  const master = slider('Master', 'master')
  const music = slider('Music', 'music')
  const sfx = slider('Sound FX', 'sfx')

  const qualitySeg = el('div', { class: 'seg' })
  const qualityButtons: Record<Quality, HTMLButtonElement> = {} as Record<Quality, HTMLButtonElement>
  ;(['low', 'medium', 'high'] as Quality[]).forEach((q) => {
    const b = el('button', { text: q[0].toUpperCase() + q.slice(1) }) as HTMLButtonElement
    b.addEventListener('click', () => {
      updateSettings({ quality: q })
      api.setQuality(q)
      syncQuality(q)
    })
    qualityButtons[q] = b
    qualitySeg.append(b)
  })
  const syncQuality = (q: Quality) => {
    for (const key of Object.keys(qualityButtons) as Quality[]) {
      qualityButtons[key].classList.toggle('is-on', key === q)
    }
  }
  const qualityField = el('div', { class: 'field' }, el('label', { text: 'Graphics' }), qualitySeg)

  // Render-resolution picker — "Native" (panel resolution) plus standard downscale modes.
  const resSelect = el('select', { class: 'res-select' }) as HTMLSelectElement
  // Rebuilt on each onShow so moving the window to a different-resolution monitor (or changing zoom)
  // re-derives the ladder + native label instead of carrying the boot-time values for the session.
  const rebuildResModes = () => {
    const native = nativePanelSize()
    resSelect.replaceChildren()
    for (const m of buildResolutionModes(native.w, native.h)) {
      resSelect.append(el('option', { text: m.label, attrs: { value: String(m.height) } }))
    }
  }
  rebuildResModes()
  resSelect.addEventListener('change', () => {
    const h = Number(resSelect.value)
    updateSettings({ resolution: h })
    api.setResolution(h)
    api.audio.play('click')
  })
  const resField = el('div', { class: 'field' }, el('label', { text: 'Resolution' }), resSelect)

  // Fullscreen toggle — mirrors the corner button; label reflects live state.
  const fsSeg = el('div', { class: 'seg' })
  const fsBtn = el('button', { text: 'Enter' }) as HTMLButtonElement
  fsBtn.addEventListener('click', () => api.toggleFullscreen())
  fsSeg.append(fsBtn)
  const fsField = el('div', { class: 'field' }, el('label', { text: 'Fullscreen' }), fsSeg)
  if (!document.fullscreenEnabled) fsField.style.display = 'none' // unsupported (e.g. iPhone Safari)
  const syncFullscreen = () => {
    const on = !!document.fullscreenElement
    fsBtn.textContent = on ? 'Exit' : 'Enter'
    fsBtn.classList.toggle('is-on', on)
  }
  // Intentionally app-lifetime: createSettingsScreen runs once during UIRoot construction, so this
  // single listener lives as long as the page. (No ScreenView teardown hook exists to remove it.)
  document.addEventListener('fullscreenchange', syncFullscreen)

  const motionSeg = el('div', { class: 'seg' })
  const motionOn = el('button', { text: 'Full' }) as HTMLButtonElement
  const motionReduced = el('button', { text: 'Reduced' }) as HTMLButtonElement
  const syncMotion = (reduced: boolean) => {
    motionOn.classList.toggle('is-on', !reduced)
    motionReduced.classList.toggle('is-on', reduced)
  }
  motionOn.addEventListener('click', () => {
    updateSettings({ reduceMotion: false })
    syncMotion(false)
  })
  motionReduced.addEventListener('click', () => {
    updateSettings({ reduceMotion: true })
    syncMotion(true)
  })
  motionSeg.append(motionOn, motionReduced)
  const motionField = el('div', { class: 'field' }, el('label', { text: 'Motion' }), motionSeg)

  const nick = el('input', { attrs: { type: 'text', maxlength: '20', placeholder: 'BlueBob' } }) as HTMLInputElement
  nick.addEventListener('input', () => api.economy.setNickname(nick.value))
  const nickField = el('div', { class: 'field' }, el('label', { text: 'Nickname' }), nick)

  const back = button('BACK', { variant: 'secondary', onClick: () => api.go('menu') })

  p.append(master.field, music.field, sfx.field, qualityField, resField, fsField, motionField, nickField, back)
  root.append(p)

  return {
    el: root,
    onShow() {
      const s = getSettings()
      master.input.value = String(s.master)
      music.input.value = String(s.music)
      sfx.input.value = String(s.sfx)
      syncQuality(s.quality)
      rebuildResModes()
      resSelect.value = String(s.resolution)
      if (resSelect.selectedIndex < 0) {
        // Saved mode no longer offered (e.g. moved to a smaller display) → reconcile to native, not
        // just the dropdown label: persist it and re-apply to the renderer so the stored value + live
        // drawing buffer match what the UI shows (else the game keeps rendering the orphaned height).
        resSelect.value = '0'
        updateSettings({ resolution: 0 })
        api.setResolution(0)
      }
      syncFullscreen()
      syncMotion(s.reduceMotion)
      nick.value = api.economy.state.nickname
    },
  }
}
